/**
 * Dependency-free search core for the metrics pickers.
 *
 * A picker owns an ordered list of display texts, one per selectable key. The
 * index scans those texts with the same query semantics as
 * `ModifiedDropdown`'s regex search and returns matching entry INDICES, so
 * callers build their dropdown option objects on demand for just the matches
 * instead of for every key on every search.
 *
 * Result contract: all exact matches first (scan order), then at most
 * `limit + 1` other matches (scan order). The first `limit` entries are
 * identical to what an uncapped scan would return, and
 * `result.length > limit` exactly when the true match count exceeds `limit`.
 * The full match set is NOT returned — callers may only slice the result and
 * compare its length against `limit`, which is all `ModifiedDropdown` does
 * with a search result (`slice(0, resultLimit)` plus the truncation message).
 *
 * Exactness is checked as `text.toLowerCase() === query.toLowerCase()` behind
 * a length gate. The gate assumes lowercasing preserves string length, which
 * holds for ASCII (metric names in practice); the rare Unicode case mappings
 * that grow a string (e.g. İ) would demote an exact match to an ordinary one.
 */

export type PickerSearch = {
  search(query: string, limit: number): number[];
};

/**
 * Mirror of `getAsValidRegex` in ModifiedDropdown.tsx, kept import-free so
 * this module has no dependencies. A parity test pins the two together.
 */
export function compileSearchRegex(query: string): RegExp | null {
  let cleaned = query.trim();

  // A single '*' means "match everything", even though it isn't a valid regex.
  if (cleaned === '*') {
    cleaned = '.*';
  }

  if (cleaned.length === 0) {
    return null;
  }

  try {
    return new RegExp(cleaned, 'i');
  } catch (e) {
    return null;
  }
}

function isEmptyQuery(query: string): boolean {
  return query.trim().length === 0;
}

function isExactMatch(text: string, queryLower: string): boolean {
  return text.length === queryLower.length && text.toLowerCase() === queryLower;
}

/** Eager core: scans `texts` per search. `texts` must not be mutated after. */
export function buildPickerSearchIndex(texts: readonly string[]): PickerSearch {
  // Lowercased texts are only needed when the query is not a valid regex
  // (matching falls back to case-insensitive substring). Built on the first
  // such query and cached: typing an unfinished regex like "(" hits this
  // path on every subsequent keystroke.
  let lowerTexts: string[] | null = null;
  const getLowerTexts = () => {
    if (lowerTexts == null) {
      lowerTexts = texts.map(text => text.toLowerCase());
    }
    return lowerTexts;
  };

  return {
    search(query: string, limit: number): number[] {
      if (isEmptyQuery(query)) {
        return [];
      }

      const regex = compileSearchRegex(query);
      const lower = regex == null ? getLowerTexts() : null;
      const queryLower = query.toLowerCase();
      const exactMatches: number[] = [];
      const matches: number[] = [];

      // Phase 1: scan until `limit + 1` non-exact matches are collected.
      // Beyond that the consumer can only observe the result's length
      // relative to `limit`, never its contents.
      let i = 0;
      for (; i < texts.length; i++) {
        const text = texts[i];
        const matchesQuery =
          regex != null ? regex.test(text) : lower![i].includes(queryLower);
        if (!matchesQuery) {
          continue;
        }
        if (isExactMatch(text, queryLower)) {
          exactMatches.push(i);
        } else {
          matches.push(i);
          if (matches.length > limit) {
            i++;
            break;
          }
        }
      }

      // Phase 2: exact matches sort ahead of everything else, so the rest of
      // the list still needs an exact-only sweep. The length gate makes this
      // a cheap pass. An exact text must still satisfy the query itself — a
      // text equal to the query can fail it as a regex (e.g. query "a$b").
      // On the substring fallback path equality implies a match, so no
      // further check is needed there.
      for (; i < texts.length; i++) {
        const text = texts[i];
        if (!isExactMatch(text, queryLower)) {
          continue;
        }
        if (regex != null && !regex.test(text)) {
          continue;
        }
        exactMatches.push(i);
      }

      if (exactMatches.length === 0) {
        return matches;
      }
      return exactMatches.concat(matches);
    },
  };
}

/**
 * Lazy wrapper: the texts (and index) are built on the first non-empty
 * search and reused for the instance lifetime. Create a new instance when
 * the underlying key set changes. This is also the seam where a background
 * prebuild could slot in without touching the pickers.
 */
export function createLazyPickerSearch(
  getTexts: () => readonly string[]
): PickerSearch {
  let index: PickerSearch | null = null;
  return {
    search(query: string, limit: number): number[] {
      // Don't build the index for the empty query `ModifiedDropdown` issues
      // when a search box is cleared.
      if (isEmptyQuery(query)) {
        return [];
      }
      if (index == null) {
        index = buildPickerSearchIndex(getTexts());
      }
      return index.search(query, limit);
    },
  };
}
